"use client";

import { usePdpVariantNavigation } from "./variant-navigation-context";
import { useFormStatus } from "react-dom";
import { useTranslations } from "next-intl";
import { ShoppingBag, Check, ShieldCheck, FileCheck, CheckCircle } from "lucide-react";
import { Button } from "@/ui/components/ui/button";
import { useAddToCartFeedback } from "./add-to-cart-form";
import { PaymentIcons } from "@/ui/components/shared/payment-icons";
import { cn } from "@/lib/utils";

interface AddToCartProps {
	disabled?: boolean;
	disabledReason?: "no-selection" | "out-of-stock";
	/**
	 * When true, render only the submit button — the parent (PDP v2 buy-box)
	 * already renders TrustRow + PaymentStrip below the TotalAtcCard, so we
	 * skip the inline duplicates here.
	 */
	minimal?: boolean;
}

function AddToCartButton({
	disabled,
	disabledReason,
}: {
	disabled?: boolean;
	disabledReason?: "no-selection" | "out-of-stock";
}) {
	const { pending } = useFormStatus();
	const t = useTranslations("product");
	const feedback = useAddToCartFeedback();
	const selectionPending = usePdpVariantNavigation()?.pending ?? false;
	const showSuccess = !selectionPending && !pending && feedback?.success === true;

	const getButtonText = () => {
		if (selectionPending) return t("variantUpdating");
		if (showSuccess) return t("addedToBag");
		if (pending) return t("adding");
		if (!disabled) return t("addToBag");
		if (disabledReason === "out-of-stock") return t("outOfStock");
		return t("selectOptions");
	};

	const Icon = showSuccess ? Check : ShoppingBag;

	return (
		<Button
			type="submit"
			data-pdp-primary-atc
			size="lg"
			disabled={disabled || pending || selectionPending}
			aria-busy={pending || selectionPending || undefined}
			className={cn(
				"group/atc h-auto min-h-12 w-full whitespace-normal rounded-full bg-primary-hero px-3 py-3 font-display text-sm font-medium leading-snug text-primary-hero-foreground shadow-md transition-colors duration-200 hover:bg-primary sm:min-h-14",
				pending && "opacity-80",
				showSuccess && "bg-success hover:bg-success",
			)}
		>
			<Icon
				className={cn("mr-2.5 h-5 w-5 shrink-0 transition-transform", pending && "scale-90")}
				aria-hidden
			/>
			{getButtonText()}
		</Button>
	);
}

export function AddToCart({ disabled = false, disabledReason, minimal = false }: AddToCartProps) {
	const t = useTranslations("product");

	if (minimal) {
		return <AddToCartButton disabled={disabled} disabledReason={disabledReason} />;
	}

	return (
		<div className="space-y-4">
			{/* Add to Cart Button */}
			<AddToCartButton disabled={disabled} disabledReason={disabledReason} />

			{/* Compact Trust Badges */}
			<div className="grid grid-cols-3 gap-2 pt-2 sm:flex sm:flex-wrap sm:justify-center sm:gap-4">
				<span className="flex items-center gap-1.5 text-xs text-muted-foreground">
					<ShieldCheck className="h-3.5 w-3.5 shrink-0" />
					{t("trustPurity")}
				</span>
				<span className="flex items-center gap-1.5 text-xs text-muted-foreground">
					<FileCheck className="h-3.5 w-3.5 shrink-0" />
					{t("trustCoa")}
				</span>
				<span className="flex items-center gap-1.5 text-xs text-muted-foreground">
					<CheckCircle className="h-3.5 w-3.5 shrink-0" />
					{t("trustGmp")}
				</span>
			</div>

			{/* Payment Methods — centered on desktop, left-aligned on narrow viewports */}
			<PaymentIcons size="sm" className="pt-1 lg:justify-center" />
		</div>
	);
}
